import React, { useCallback, useState, useEffect } from 'react';
import { Box, Typography, Select, MenuItem, InputBase, useTheme } from '@mui/material';
import { useFormPalette } from '../pages/forms/DynamicFormPrimitives';
import { NODE_UI } from '../NodeUI/nodeUiStyles';
import PeoplePicker from '../common/PeoplePicker';

const ACCESS_MODES = [
  { value: 'DISABLED', label: 'Disabled' },
  { value: 'PUBLIC', label: 'Public' },
  { value: 'EMAIL_MAGIC_LINK', label: 'Email Magic Link' },
  { value: 'AUTHENTICATED_USERS', label: 'Authenticated Users' },
  { value: 'AUTHENTICATED_SELECTED_USERS', label: 'Authenticated Selected Users' },
];

const MODE_DESCRIPTIONS = {
  DISABLED: 'Access is disabled. No public/share-link access is allowed.',
  PUBLIC: 'Anyone with the link can access using a stable anonymous identity.',
  EMAIL_MAGIC_LINK: 'Users must verify their email via a magic link before accessing.',
  AUTHENTICATED_USERS: 'Only signed-in users can access.',
  AUTHENTICATED_SELECTED_USERS: 'Only specific signed-in users can access.',
};

const EMAIL_RESTRICTION_OPTIONS = [
  { value: 'ANY', label: 'Any email' },
  { value: 'ALLOWLIST', label: 'Specific emails only' },
  { value: 'DOMAIN', label: 'Specific domains only' },
];

const EMAIL_RESTRICTION_DESCRIPTIONS = {
  ANY: 'Any valid email address can be verified.',
  ALLOWLIST: 'Only listed email addresses can be verified.',
  DOMAIN: 'Only email addresses from listed domains can be verified.',
};

const selectSx = (palette, theme) => ({
  fontFamily: 'inherit',
  fontSize: NODE_UI.fontSize.value,
  color: palette.textPrimary,
  backgroundColor: theme.palette.background.default,
  borderRadius: NODE_UI.borderRadiusSm,
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: palette.border,
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: palette.textSecondary,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: palette.textPrimary,
  },
  '& .MuiSelect-select': {
    padding: NODE_UI.input.padding,
  },
});

function FieldLabel({ children, palette }) {
  return (
    <Typography
      component="label"
      sx={{
        fontSize: NODE_UI.fontSize.label,
        fontWeight: NODE_UI.fontWeight.label,
        color: palette.textSecondary,
        lineHeight: 1.4,
        userSelect: 'none',
      }}
    >
      {children}
    </Typography>
  );
}

function HintText({ children, palette }) {
  return (
    <Typography
      sx={{
        fontSize: NODE_UI.fontSize.caption,
        color: palette.textSecondary,
        opacity: 0.75,
      }}
    >
      {children}
    </Typography>
  );
}

function MultilineInput({ value, onChange, onBlur, placeholder, palette }) {
  return (
    <InputBase
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      placeholder={placeholder}
      multiline
      minRows={2}
      maxRows={6}
      fullWidth
      sx={{
        fontFamily: 'inherit',
        fontSize: NODE_UI.fontSize.value,
        color: palette.textPrimary,
        border: `1px solid ${palette.border}`,
        borderRadius: NODE_UI.borderRadiusSm,
        px: '8px',
        py: '5px',
        transition: 'border-color 0.15s ease',
        '&:hover': { borderColor: palette.textSecondary },
        '&.Mui-focused': { borderColor: palette.textPrimary },
        '& textarea::placeholder': { color: palette.textSecondary, opacity: 0.5 },
      }}
    />
  );
}

export default function PublicAccessPolicyEditor({
  accessPolicy,
  onChange,
  users = [],
  usersLoading = false,
  disabled = false,
  modes = ACCESS_MODES,
}) {
  const palette = useFormPalette();
  const theme = useTheme();

  const policy = accessPolicy || { mode: 'PUBLIC', selectedUserIds: [] };
  const mode = policy.mode || 'PUBLIC';
  const selectedUserIds = Array.isArray(policy.selectedUserIds) ? policy.selectedUserIds : [];
  const emailRestriction = policy.emailRestriction || 'ANY';
  const allowedEmails = Array.isArray(policy.allowedEmails) ? policy.allowedEmails : [];
  const allowedDomains = Array.isArray(policy.allowedDomains) ? policy.allowedDomains : [];

  const emit = useCallback(
    (patch) => onChange({ ...policy, ...patch }),
    [policy, onChange],
  );

  const handleModeChange = (e) => {
    const newMode = e.target.value;
    emit({
      mode: newMode,
      selectedUserIds: newMode === 'AUTHENTICATED_SELECTED_USERS' ? selectedUserIds : [],
      emailRestriction: newMode === 'EMAIL_MAGIC_LINK' ? emailRestriction : 'ANY',
      allowedEmails: newMode === 'EMAIL_MAGIC_LINK' ? allowedEmails : [],
      allowedDomains: newMode === 'EMAIL_MAGIC_LINK' ? allowedDomains : [],
    });
  };

  const handleUserToggle = (userId) => {
    const next = selectedUserIds.includes(userId)
      ? selectedUserIds.filter((id) => id !== userId)
      : [...selectedUserIds, userId];
    emit({ selectedUserIds: next });
  };

  const handleEmailRestrictionChange = (e) => {
    const value = e.target.value;
    emit({
      emailRestriction: value,
      allowedEmails: value === 'ALLOWLIST' ? allowedEmails : [],
      allowedDomains: value === 'DOMAIN' ? allowedDomains : [],
    });
  };

  const parseLines = (text) =>
    text.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);

  // Local text state for the multiline inputs — parse only on blur
  const [emailsText, setEmailsText] = useState(() => allowedEmails.join('\n'));
  const [domainsText, setDomainsText] = useState(() => allowedDomains.join('\n'));

  // Sync from props when the array changes externally (e.g. mode switch)
  useEffect(() => { setEmailsText(allowedEmails.join('\n')); }, [allowedEmails.join('\n')]);
  useEffect(() => { setDomainsText(allowedDomains.join('\n')); }, [allowedDomains.join('\n')]);

  const sx = selectSx(palette, theme);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <FieldLabel palette={palette}>Access Mode</FieldLabel>
      <Select value={mode} onChange={handleModeChange} size="small" disabled={disabled} sx={sx}>
        {modes.map((m) => (
          <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
        ))}
      </Select>
      {MODE_DESCRIPTIONS[mode] && <HintText palette={palette}>{MODE_DESCRIPTIONS[mode]}</HintText>}

      {/* ── Email magic link restriction ─────────────────────────── */}
      {mode === 'EMAIL_MAGIC_LINK' && (
        <Box sx={{ mt: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <FieldLabel palette={palette}>Email Restriction</FieldLabel>
          <Select
            value={emailRestriction}
            onChange={handleEmailRestrictionChange}
            size="small"
            disabled={disabled}
            sx={sx}
          >
            {EMAIL_RESTRICTION_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
          <HintText palette={palette}>
            {EMAIL_RESTRICTION_DESCRIPTIONS[emailRestriction]}
          </HintText>

          {emailRestriction === 'ALLOWLIST' && (
            <Box sx={{ mt: '4px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <FieldLabel palette={palette}>Allowed Emails</FieldLabel>
              <MultilineInput
                value={emailsText}
                onChange={(e) => setEmailsText(e.target.value)}
                onBlur={() => emit({ allowedEmails: parseLines(emailsText) })}
                placeholder={'user@example.com\nanother@example.com'}
                palette={palette}
              />
              <HintText palette={palette}>One email per line or comma-separated.</HintText>
              {allowedEmails.length === 0 && (
                <Typography sx={{ fontSize: NODE_UI.fontSize.caption, color: palette.errorText }}>
                  At least one email is required.
                </Typography>
              )}
            </Box>
          )}

          {emailRestriction === 'DOMAIN' && (
            <Box sx={{ mt: '4px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <FieldLabel palette={palette}>Allowed Domains</FieldLabel>
              <MultilineInput
                value={domainsText}
                onChange={(e) => setDomainsText(e.target.value)}
                onBlur={() => emit({ allowedDomains: parseLines(domainsText) })}
                placeholder={'acme.com\nexample.org'}
                palette={palette}
              />
              <HintText palette={palette}>One domain per line or comma-separated.</HintText>
              {allowedDomains.length === 0 && (
                <Typography sx={{ fontSize: NODE_UI.fontSize.caption, color: palette.errorText }}>
                  At least one domain is required.
                </Typography>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* ── Selected users picker ────────────────────────────────── */}
      {mode === 'AUTHENTICATED_SELECTED_USERS' && (
        <Box sx={{ mt: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <PeoplePicker
            items={users}
            selectedIds={selectedUserIds}
            onToggle={handleUserToggle}
            getId={(u) => u.userName}
            getName={(u) => u.displayName || u.userName}
            getDetail={(u) => u.email || u.userName}
            getAvatarLabel={(u) => u.displayName || u.userName || 'User'}
            loading={usersLoading}
            disabled={disabled}
            placeholder="Search for users..."
            emptyStateLabel="No users available"
          />
          {selectedUserIds.length === 0 && (
            <Typography sx={{ fontSize: NODE_UI.fontSize.caption, color: palette.errorText }}>
              At least one user must be selected for this access mode.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
